/**
 * Campaign Performance — column model.
 *
 * Same shape as team-performance/columns.ts (`stickyLeft` pins to the left
 * edge, `locked` means "not hideable") so this report inherits the same table
 * behaviour. Campaign Name is the pinned identity column, exactly as Team Name
 * is in Team Performance.
 */
import {
  Clock, Megaphone, Hash, Sparkles, UserCheck, UserX, CheckCircle2, RotateCcw,
  ArrowLeftRight, ShieldCheck, GitMerge, AlertTriangle, Timer, Percent,
} from 'lucide-react'
import type { ReportColumnDef } from '../agent-performance/columns.js'

export type { ReportColumnDef } from '../agent-performance/columns.js'

/** Report id — the key this report's saved view is stored under on the user. */
export const CAMPAIGN_PERFORMANCE_REPORT_ID = 'conversation.campaign-performance'

export const CAMPAIGN_PERFORMANCE_COLUMNS: ReportColumnDef[] = [
  { key: 'campaignName', label: 'Campaign Name', width: 240, stickyLeft: 0, locked: true, icon: Megaphone },
  { key: 'total', label: 'Total', width: 160, align: 'right', icon: Hash },
  { key: 'newCount', label: 'New', width: 160, align: 'right', icon: Sparkles },
  { key: 'assigned', label: 'Assigned', width: 180, align: 'right', icon: UserCheck },
  { key: 'unassigned', label: 'Unassigned', width: 180, align: 'right', icon: UserX },
  { key: 'resolved', label: 'Resolved', width: 170, align: 'right', icon: CheckCircle2 },
  { key: 'reOpened', label: 'Re Opened', width: 170, align: 'right', icon: RotateCcw },
  { key: 'transferred', label: 'Transferred', width: 170, align: 'right', icon: ArrowLeftRight },
  { key: 'primary', label: 'Primary', width: 170, align: 'right', icon: ShieldCheck },
  { key: 'mergedAsSecondary', label: 'Merged as Secondary', width: 220, align: 'right', icon: GitMerge },
  { key: 'avgWaitTime', label: 'Avg Wait Time', width: 170, align: 'right', icon: Clock },
  { key: 'withinResponseSla', label: 'Within Response SLA', width: 190, align: 'right', icon: CheckCircle2 },
  { key: 'withinResolvedSla', label: 'Within Resolved SLA', width: 190, align: 'right', icon: CheckCircle2 },
  { key: 'responseEscalated', label: 'Response Escalated', width: 180, align: 'right', icon: AlertTriangle },
  { key: 'resolutionEscalated', label: 'Resolution Escalated', width: 180, align: 'right', icon: AlertTriangle },
  { key: 'avgResponseTime', label: 'Avg Response Time', width: 180, align: 'right', icon: Timer },
  { key: 'avgResolutionTime', label: 'Avg Resolution Time', width: 180, align: 'right', icon: Timer },
  { key: 'responseSlaPct', label: 'Response SLA %', width: 160, align: 'right', icon: Percent },
  { key: 'resolutionSlaPct', label: 'Resolution SLA %', width: 160, align: 'right', icon: Percent },
]

/** Everything visible, in catalog order — what a user sees before saving a view. */
export const DEFAULT_VISIBLE_COLUMNS: string[] = CAMPAIGN_PERFORMANCE_COLUMNS.map((c) => c.key)

/**
 * Reconcile a stored view against the current catalog. Unknown keys are
 * dropped, locked columns are forced back in, and an empty result falls back
 * to the default set. See agent-performance/columns.ts for the full rationale
 * — this is the same function, re-keyed to this report's catalog.
 */
export function sanitizeColumnOrder(saved: unknown): string[] {
  if (!Array.isArray(saved)) return DEFAULT_VISIBLE_COLUMNS
  const known = new Set(CAMPAIGN_PERFORMANCE_COLUMNS.map((c) => c.key))
  const seen = new Set<string>()
  const ordered: string[] = []
  for (const key of saved) {
    if (typeof key !== 'string' || !known.has(key) || seen.has(key)) continue
    seen.add(key)
    ordered.push(key)
  }
  for (const c of CAMPAIGN_PERFORMANCE_COLUMNS) {
    if (c.locked && !seen.has(c.key)) {
      seen.add(c.key)
      ordered.unshift(c.key)
    }
  }
  return ordered.length > 0 ? ordered : DEFAULT_VISIBLE_COLUMNS
}
